import { useState, useEffect, useCallback } from "react";
import {
  KIND_LABEL, STATUS_LABEL, excludedLines, campaignLine, approveQuestion, canDecide,
} from "./campaignCopy";

// THE APPROVAL QUEUE FOR BULK EMAIL AND REMINDERS (2026-11-02). Approval is only worth having if the approver can see
// what is being approved, so it shows the people by name and address, who was left out and why, and the exact subject and
// body each person will get. A body is drawn in a sandboxed frame: it holds HTML, and scripts must not run here.
const box = { border: "1px solid #ddd", borderRadius: 6, padding: 12, marginTop: 12 };
const scroll = { maxHeight: 280, overflowY: "auto", fontSize: 13 };

async function call(apiUrl, path, options = {}) {
  const res = await fetch(`${apiUrl}/api/admin/mail-campaigns${path}`, {
    credentials: "include", ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || `Request failed (${res.status})`);
  return body;
}

function Review({ apiUrl, campaignId, viewerCanDecide, onChanged }) {
  const [doc, setDoc] = useState(null);
  const [picked, setPicked] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setDoc(await call(apiUrl, `/${campaignId}`));
    } catch (e) {
      setError(e.message);
    }
  }, [apiUrl, campaignId]);
  useEffect(() => { load(); }, [load]);

  if (error && !doc) return <div style={box}>{error}</div>;
  if (!doc) return <div style={box}>Loading…</div>;
  const person = (doc.recipients || [])[picked];

  async function decide(action, body) {
    setBusy(true);
    setError("");
    try {
      await call(apiUrl, `/${campaignId}/${action}`, { method: "POST", body: JSON.stringify(body || {}) });
      await load();
      onChanged();
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  }

  function approve() {
    if (!window.confirm(approveQuestion(doc))) return;
    decide("approve", { content_hash: doc.content_hash, recipient_count: doc.recipient_count });
  }

  return (
    <div style={box} data-testid="mail-campaign-review">
      <p><b>Subject:</b> {doc.subject}</p>
      <p><b>Audience:</b> {doc.audience}. Prepared by {doc.prepared_by}.</p>

      <p><b>Will receive it ({doc.recipient_count})</b>. Pick a person to read exactly what they will get.</p>
      <div style={scroll}>
        <table style={{ width: "100%" }}>
          <tbody>
            {doc.recipients.map((r, i) => (
              <tr key={r.email} onClick={() => setPicked(i)}
                  style={{ cursor: "pointer", background: i === picked ? "#eef6ee" : "transparent" }}>
                <td>{r.name || "(no name)"}</td><td>{r.email}</td><td>{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: 12 }}>
        <b>Left out ({(doc.excluded || []).length})</b>
        {excludedLines(doc.excluded_counts).length ? `: ${excludedLines(doc.excluded_counts).join(", ")}` : ""}
      </p>
      <details>
        <summary>Show who was left out</summary>
        <div style={scroll}>
          <table style={{ width: "100%" }}>
            <tbody>
              {(doc.excluded || []).map((x) => (
                <tr key={`${x.email}-${x.reason}`}><td>{x.name || "(no name)"}</td><td>{x.email}</td><td>{x.reason}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      {person && (
        <>
          <p style={{ marginTop: 12 }}><b>Exactly what {person.name || person.email} gets</b></p>
          <p><b>Subject:</b> {person.subject}</p>
          <iframe title="Email body" sandbox="" srcDoc={person.html}
                  style={{ width: "100%", height: 340, border: "1px solid #ddd" }} />
          <details>
            <summary>Plain-text version</summary>
            <pre style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{person.text}</pre>
          </details>
        </>
      )}

      {error && <p style={{ color: "#b00020" }}>{error}</p>}
      {doc.status === "awaiting_approval" && !viewerCanDecide && (
        <p>Only the approver can approve or reject this.</p>
      )}
      {canDecide(doc, viewerCanDecide) && (
        <div className="bulk-email-actions">
          <button className="btn btn-primary" onClick={approve} disabled={busy}>
            {busy ? "Working..." : `Approve and send to ${doc.recipient_count}`}
          </button>
          <button className="btn btn-secondary" onClick={() => decide("reject")} disabled={busy}>Reject</button>
        </div>
      )}
      {(doc.results || []).length > 0 && (
        <details open>
          <summary>Results ({campaignLine(doc)})</summary>
          <div style={scroll}>
            {doc.results.map((r) => <div key={r.email}>{r.email}: {r.sent ? "sent" : r.outcome || "failed"}</div>)}
          </div>
        </details>
      )}
    </div>
  );
}

export default function MailCampaigns({ apiUrl, refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [viewerCanDecide, setViewerCanDecide] = useState(false);
  const [openId, setOpenId] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await call(apiUrl, "");
      setRows(d.campaigns || []);
      setViewerCanDecide(!!d.viewer_can_decide);
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }, [apiUrl]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="admin-card" data-testid="mail-campaigns">
      <div className="section-title">Waiting for approval, and past sends</div>
      <p className="section-sub">Nothing above sends by itself. Each one waits here until it is approved.</p>
      {error && <p style={{ color: "#b00020" }}>Could not read the queue: {error}</p>}
      {!error && rows.length === 0 && <p>Nothing prepared yet.</p>}
      {rows.map((c) => (
        <div key={c.campaign_id} style={box}>
          <b>{KIND_LABEL[c.kind] || c.kind}</b>: {c.subject}
          <div>{STATUS_LABEL[c.status] || c.status}. {campaignLine(c)}.</div>
          <button className="btn btn-secondary" onClick={() => setOpenId(openId === c.campaign_id ? "" : c.campaign_id)}>
            {openId === c.campaign_id ? "Close" : "Open and read"}
          </button>
          {openId === c.campaign_id && (
            <Review apiUrl={apiUrl} campaignId={c.campaign_id} viewerCanDecide={viewerCanDecide} onChanged={load} />
          )}
        </div>
      ))}
    </div>
  );
}
